/**
 * ABA validation utilities
 * Comprehensive validation rules for originator bank details and security measures
 */

import ABAApi from "../data/api/abaApi";
import { OriginatorBankFormData, OriginatorBankValidation } from "../types/aba";

/**
 * Validate BSB and get institution name (synchronous version with fallback)
 */
export const validateBSB = (bsb: string): {
  isValid: boolean;
  error?: string;
  institutionName?: string;
  formatted?: string
} => {
  if (!bsb) {
    return { isValid: false, error: 'BSB is required' };
  }

  // Remove any formatting
  const cleanBSB = bsb.replace(/\D/g, '');

  if (cleanBSB.length !== 6) {
    return { isValid: false, error: 'BSB must be 6 digits' };
  }

  // Format BSB with dash
  const formatted = `${cleanBSB.slice(0, 3)}-${cleanBSB.slice(3)}`;

  // Basic range validation - valid BSB ranges typically start between 010-999
  const firstThree = parseInt(cleanBSB.slice(0, 3));
  if (firstThree < 10 || firstThree > 999) {
    return {
      isValid: false,
      error: 'BSB appears to be invalid - check with your bank'
    };
  }

  return {
    isValid: true,
    formatted,
    institutionName: 'Unknown Bank'
  };
};

/**
 * Validate BSB using real API (asynchronous version)
 */
export const validateBSBAsync = async (bsb: string): Promise<{
  isValid: boolean;
  error?: string;
  institutionName?: string;
  formatted?: string
}> => {
  if (!bsb) {
    return { isValid: false, error: 'BSB is required' };
  }

  // Remove any formatting
  const cleanBSB = bsb.replace(/\D/g, '');

  if (cleanBSB.length !== 6) {
    return { isValid: false, error: 'BSB must be 6 digits' };
  }

  // Format BSB with dash
  const formatted = `${cleanBSB.slice(0, 3)}-${cleanBSB.slice(3)}`;

  try {
    // Use real API for validation
    const result = await ABAApi.lookupBSB(cleanBSB);

    if (result.success && result.data) {
      return {
        isValid: true,
        formatted,
        institutionName: result.data.institutionName || result.data.financialInstitution || 'Bank'
      };
    } else {
      // Fallback to local validation
      return validateBSB(bsb);
    }
  } catch (error) {
    // Fallback to local validation if API fails
    return validateBSB(bsb);
  }
};

/**
 * Validation rules configuration
 */
export const ABA_VALIDATION_RULES = {
  bsb: {
    pattern: /^\d{6}$|^\d{3}-\d{3}$/,
    maxLength: 7, // Including dash
    message: 'BSB must be 6 digits (e.g., 062000 or 062-000)',
    format: (value: string) => {
      const cleaned = value.replace(/\D/g, '');
      return cleaned.length === 6 ? `${cleaned.slice(0, 3)}-${cleaned.slice(3)}` : value;
    },
  },
  accountNumber: {
    pattern: /^\d{1,9}$/,
    maxLength: 9,
    message: 'Account number must be up to 9 digits',
    format: (value: string) => value.padStart(9, '0'),
  },
  accountName: {
    maxLength: 32,
    pattern: /^[A-Z0-9\s\-\.&']+$/i,
    message: 'Account name must be 32 characters or less. Only letters, numbers, spaces, -, ., &, \' allowed',
    format: (value: string) => value.toUpperCase().trim(),
  },
  remitterName: {
    maxLength: 16,
    pattern: /^[A-Z0-9\s\-\.&']+$/i,
    message: 'Remitter name must be 16 characters or less. Only letters, numbers, spaces, -, ., &, \' allowed',
    format: (value: string) => value.toUpperCase().trim(),
  },
  userNumber: {
    pattern: /^\d{1,6}$/,
    maxLength: 6,
    message: 'User number must be up to 6 digits',
    format: (value: string) => value,
  },
};

/**
 * Person bank details validation function
 */
export const validatePersonBankField = (
  field: 'bsb' | 'accountNumber' | 'accountName',
  value: string
): { isValid: boolean; error?: string; formatted?: string; institutionName?: string } => {
  if (!value) {
    return {
      isValid: false,
      error: `${field.charAt(0).toUpperCase() + field.slice(1)} is required`,
    };
  }

  if (field === 'bsb') {
    return validateBSB(value);
  }

  const rules = ABA_VALIDATION_RULES[field];
  if (!rules) {
    return { isValid: false, error: 'Unknown field' };
  }

  // Check length
  if (value.length > rules.maxLength) {
    return { isValid: false, error: rules.message };
  }

  // Check pattern
  if (!rules.pattern.test(value)) {
    return { isValid: false, error: rules.message };
  }

  // Return valid with formatted value
  return {
    isValid: true,
    formatted: rules.format(value),
  };
};

/**
 * Validate a single field (legacy function, supports both originator and person bank details)
 */
export const validateABAField = (
  field: keyof OriginatorBankFormData | 'bsb' | 'accountNumber' | 'accountName',
  value: string
): { isValid: boolean; error?: string; formatted?: string; institutionName?: string } => {
  // For person bank detail fields, use the enhanced validation
  if (field === 'bsb' || field === 'accountNumber' || field === 'accountName') {
    return validatePersonBankField(field, value);
  }

  // For originator bank fields, use the original logic
  if (!value) {
    return {
      isValid: false,
      error: `${field.charAt(0).toUpperCase() + field.slice(1)} is required`,
    };
  }

  const rules = ABA_VALIDATION_RULES[field as keyof typeof ABA_VALIDATION_RULES];
  if (!rules) {
    return { isValid: false, error: 'Unknown field' };
  }

  // Check length
  if (value.length > rules.maxLength) {
    return { isValid: false, error: rules.message };
  }

  // Check pattern
  if (!rules.pattern.test(value)) {
    return { isValid: false, error: rules.message };
  }

  // Return valid with formatted value
  return {
    isValid: true,
    formatted: rules.format(value),
  };
};

/**
 * Validate complete originator bank form
 */
export const validateOriginatorBankForm = (
  formData: OriginatorBankFormData
): OriginatorBankValidation => {
  const validation: OriginatorBankValidation = {
    bsb: validateABAField('bsb', formData.bsb),
    accountNumber: validateABAField('accountNumber', formData.accountNumber),
    accountName: validateABAField('accountName', formData.accountName),
    remitterName: validateABAField('remitterName', formData.remitterName),
    userNumber: validateABAField('userNumber', formData.userNumber),
    isFormValid: false,
  };

  // Check if all fields are valid
  validation.isFormValid = Object.values(validation)
    .filter(v => typeof v === 'object' && 'isValid' in v)
    .every(v => v.isValid);

  return validation;
};

/**
 * Clean sensitive data from form state
 * Should be called after successful ABA generation
 */
export const clearSensitiveData = (
  setFormData: (data: OriginatorBankFormData) => void,
  setValidation?: (validation: OriginatorBankValidation | undefined) => void
): void => {
  // Clear form data
  setFormData({
    bsb: '',
    accountNumber: '',
    accountName: '',
    remitterName: '',
    userNumber: '',
  });

  // Clear validation state
  if (setValidation) {
    setValidation(undefined);
  }

  // Clear any potential memory references
  if (typeof window !== 'undefined' && window.gc && process.env.NODE_ENV === 'development') {
    // Force garbage collection if available (development only)
    window.gc();
  }
};

/**
 * Mask sensitive data for logging/error reporting
 */
export const maskSensitiveData = (data: Partial<OriginatorBankFormData>): Partial<OriginatorBankFormData> => {
  return {
    bsb: data.bsb ? `${data.bsb.substring(0, 3)}***` : undefined,
    accountNumber: data.accountNumber ? `****${data.accountNumber.slice(-4)}` : undefined,
    accountName: data.accountName ? `${data.accountName.substring(0, 3)}***` : undefined,
    remitterName: data.remitterName,
    userNumber: data.userNumber ? '***' : undefined,
  };
};

/**
 * Security validation for ABA operations
 */
export const validateSecurityContext = (): { isSecure: boolean; warnings: string[] } => {
  const warnings: string[] = [];
  let isSecure = true;

  // Check if we're running over HTTPS (except localhost)
  if (typeof window !== 'undefined') {
    const isLocalhost = window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    if (!window.location.protocol.startsWith('https') && !isLocalhost) {
      warnings.push('ABA operations should only be performed over HTTPS');
      isSecure = false;
    }

    // Check for development tools
    if (window.devtools && window.devtools.open) {
      warnings.push('Developer tools are open - ensure no sensitive data is visible');
    }

    // Check localStorage for any ABA-related keys
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('aba') || key.includes('bank') || key.includes('originator'))) {
          warnings.push(`Potentially sensitive data found in localStorage: ${key}`);
        }
      }
    } catch (e) {
      // localStorage access denied
    }

    // Check sessionStorage for any ABA-related keys
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.includes('aba') || key.includes('bank') || key.includes('originator'))) {
          warnings.push(`Potentially sensitive data found in sessionStorage: ${key}`);
        }
      }
    } catch (e) {
      // sessionStorage access denied
    }
  }

  return { isSecure, warnings };
};

/**
 * Validate file content is proper base64
 */
export const validateABAFileContent = (content: string): boolean => {
  try {
    // Trim whitespace and check format
    const trimmedContent = content.trim();
    
    // Use proper base64 regex pattern
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!base64Regex.test(trimmedContent)) {
      return false;
    }

    // Check if it can be decoded
    const decoded = atob(trimmedContent);

    // Basic ABA file validation - should contain specific patterns
    if (decoded.includes('0') && decoded.length > 100) {
      return true;
    }

    return false;
  } catch (e) {
    return false;
  }
};

/**
 * Security check before ABA operations
 */
export const performSecurityCheck = (): Promise<boolean> => {
  return new Promise((resolve) => {
    const { isSecure, warnings } = validateSecurityContext();

    if (warnings.length > 0) {
      console.warn('ABA Security warnings:', warnings);
    }

    // In production, might want to block operations if not secure
    resolve(isSecure);
  });
};
